import { useState } from 'react';
import { Download, Eye, Trash2 } from 'lucide-react';
import { useLang } from '../../LangContext';
import { statusColor } from '../../utils/statusColor';
import { statusLabel } from '../../utils/statusLabel';
import { formatDate } from '../../utils/dateFormat';
import { useSubmissionFilters } from './submissions/useSubmissionFilters';
import SubmissionFilterBar from './submissions/SubmissionFilterBar';
import SubmissionDetailModal from './submissions/SubmissionDetailModal';

export default function SubmissionPage({ submissions, statuses, forms, loadSubmission, changeStatus, clearSubmissionFiles }) {
  const { locale: t, lang } = useLang();
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const {
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    formOptions,
    userOptions,
    filteredSubmissions,
    activeFilterCount,
    toggleMulti,
    resetFilters,
  } = useSubmissionFilters(submissions, forms, statuses);

  async function openSubmission(id) {
    try {
      const detail = await loadSubmission(id);
      if (detail) setSelectedSubmission(detail);
    } catch {
      // loadSubmission kendi alert'ini gösterir
    }
  }

  // Liste satırından dosyaları temizle (modal'ı açmadan)
  async function handleListClearFiles(submissionId) {
    if (!window.confirm(t.submissions.confirmClear)) return;
    try {
      await clearSubmissionFiles(submissionId);
    } catch {
      // clearSubmissionFiles kendi alert'ini gösterir
    }
  }

  // Liste satırından gönderim ekli dosyalarını toplu indir
  async function handleListDownload(submissionId) {
    try {
      const detail = await loadSubmission(submissionId);
      if (!detail) return;
      const files = detail.files || [];
      if (files.length === 0) {
        window.alert(t.submissions.noFiles);
        return;
      }
      // Her dosya için tarayıcı üzerinden indirme tetikle
      files.forEach((file, index) => {
        const url = file.downloadUrl || file.url;
        if (!url) return;
        setTimeout(() => {
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = file.originalFileName || '';
          anchor.rel = 'noreferrer';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }, index * 250); // tarayicının art arda indirmeleri engellememesi için küçük gecikme
      });
    } catch {
      // loadSubmission kendi alert'ini gösterir
    }
  }

  return (
    <section className="section page-panel sp-page">
      <div className="section-header sp-header">
        <div>
          <h2>{t.submissions.title}</h2>
          <p className="muted">{t.submissions.subtitle}</p>
        </div>
        <div className="sp-header-actions">
          <span className="metric-pill">{t.submissions.records(filteredSubmissions.length, submissions.length)}</span>
        </div>
      </div>

      <SubmissionFilterBar
        filters={filters}
        setFilters={setFilters}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        formOptions={formOptions}
        userOptions={userOptions}
        statuses={statuses}
        activeFilterCount={activeFilterCount}
        toggleMulti={toggleMulti}
        resetFilters={resetFilters}
      />

      <SubmissionListView
        items={filteredSubmissions}
        onOpen={openSubmission}
        onClear={handleListClearFiles}
        onDownload={handleListDownload}
        t={t.submissions}
        lang={lang}
      />

      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          statuses={statuses}
          onClose={() => setSelectedSubmission(null)}
          onChangeStatus={changeStatus}
          onClearFiles={clearSubmissionFiles}
          onUpdated={setSelectedSubmission}
          lang={lang}
        />
      )}
    </section>
  );
}

// ----- Liste görünümü -----
function SubmissionListView({ items, onOpen, onClear, onDownload, t, lang }) {
  const { locale } = useLang();
  if (items.length === 0) {
    return <div className="empty-state">{t.noResults}</div>;
  }
  return (
    <div className="sp-table-wrap">
      <table className="table sp-table">
        <thead>
          <tr>
            <th>{t.colForm}</th>
            <th>{t.colUser}</th>
            <th>{t.colStatus}</th>
            <th>{t.colSubmitted}</th>
            <th>{t.colUpdated}</th>
            <th className="sp-table-actions-th">{t.colActions}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const color = statusColor(item.statusCode);
            return (
              <tr key={item.id}>
                <td>{item.formName}</td>
                <td>{item.userName}</td>
                <td>
                  <span
                    className="sp-status-pill"
                    style={{ background: color.bg, borderColor: color.border, color: color.text }}
                  >
                    <span className="sp-chip-dot" style={{ background: color.text }} />
                    {statusLabel(locale, item.statusCode, item.status)}
                  </span>
                </td>
                <td>{formatDate(item.submittedAt, lang)}</td>
                <td>{item.updatedAt ? formatDate(item.updatedAt, lang) : '-'}</td>
                <td>
                  <div className="sp-row-actions">
                    <button
                      type="button"
                      className="sp-row-btn"
                      onClick={() => onDownload(item.id)}
                      title={t.download}
                    >
                      <Download size={14} />
                      <span>{t.download}</span>
                    </button>
                    <button
                      type="button"
                      className="sp-row-btn sp-row-btn-danger"
                      onClick={() => onClear(item.id)}
                      title={t.clearFiles}
                    >
                      <Trash2 size={14} />
                      <span>{t.clearFiles}</span>
                    </button>
                    <button
                      type="button"
                      className="sp-row-btn sp-row-btn-primary"
                      onClick={() => onOpen(item.id)}
                      title={t.inspect}
                    >
                      <Eye size={14} />
                      <span>{t.inspect}</span>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
